export const meta = {
  id: '50-targaryens-rhaenyra-daemon',
  order: 50,
  titleFi: 'Targaryenien kieli II: Rhaenyra ja Daemon',
  titleEn: 'The Targaryen tongue II: Rhaenyra and Daemon',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['laodikio', 'upyghagon', 'sȳndror', 'zaldrīzdōron', 'sīlāvose', 'indirarion', 'manaeragon', 'ābrazȳrys', 'bībos', 'mazumbilla', 'māzīlare', 'ours:ossȳngy', 'ours:qrimvestrio', 'ours:hīnnon', 'ours:āsomburnon', 'ours:pikilla', 'ours:arademmagon', 'ours:drīvuragon', 'ours:arademmarion', 'ours:satana', 'ours:pamio', 'ours:nopāzmītsos', 'ours:logrāzma'],
  exampleIds: ['hotd-s1e02-001', 'hotd-s1e02-002', 'hotd-s1e02-006', 'hotd-s1e02-007', 'hotd-s1e02-008', 'hotd-s1e02-009', 'hotd-s1e02-010', 'hotd-s1e02-011', 'hotd-s1e02-012', 'hotd-s1e02-013', 'hotd-s1e02-014', 'hotd-s1e02-015', 'hotd-s1e02-016', 'hotd-s1e02-017', 'hotd-s1e02-018', 'hotd-s1e07-012', 'hotd-s1e07-013', 'ex-27cf31bdb15b', 'ex-f1f08370505a'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_kinship_terminology',
    'https://wiki.languageinvention.com/index.php?title=Kejo_syt',
    'https://wiki.languageinvention.com/index.php?title=Iderēbagon',
    'https://wiki.languageinvention.com/index.php?title=Iqighagon',
    'https://wiki.languageinvention.com/index.php?title=Brōzagon',
    'https://wiki.languageinvention.com/index.php?title=Vilagon',
    'https://wiki.languageinvention.com/index.php?title=Dīnagon',
    'https://wiki.languageinvention.com/index.php?title=Upyghagon',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Derivational_Affixes',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  showLines: ['hotd-s1e02-001', 'hotd-s1e02-002', 'hotd-s1e02-006', 'hotd-s1e02-007', 'hotd-s1e02-008', 'hotd-s1e02-009', 'hotd-s1e02-010', 'hotd-s1e02-011', 'hotd-s1e02-012', 'hotd-s1e02-013', 'hotd-s1e02-014', 'hotd-s1e02-015', 'hotd-s1e02-016', 'hotd-s1e02-017', 'hotd-s1e02-018', 'hotd-s1e07-012', 'hotd-s1e07-013'],
  pronunciation: { focus: 'rhythm', words: ['drakarys', 'Targārien', 'zaldrīzdōron'] },
  letter: {
    fi: 'Kirjoita hahmosi vala tai uhkaus Rhaenyran ja Daemonin tyyliin.',
    en: 'Write your character’s oath or threat in the style of Rhaenyra and Daemon.',
  },
  scene: 'hotd-s1e02-1',
  // the Halloween script (docs/COURSE.md "The Halloween script")
  halloween: ['esitys'],
  // Halloween-mittari (docs/COURSE.md): the meter's sentence on this week's hub
  halloweenMeter: true,
}

# Targaryenien kieli II: Rhaenyra ja Daemon

Toisella lukutunnilla siirrytään House of the Dragonin ensimmäisen kauden toiseen jaksoon. Daemon on vienyt lohikäärmeenmunan Lohikäärmekivelle, ja nuori Rhaenyra lentää perässä hakemaan sitä takaisin. Edellisellä oppitunnilla Daenerys piti puheita. Nyt kuullaan **keskustelua**: lyhyitä repliikkejä, kysymyksiä, piikittelyä ja vastaväitteitä. Juuri tällaista kieltä tarvitsette, kun puhutte keskenänne.

Lue samalla tavalla kuin edellisellä oppitunnilla: ensin repliikki ja ääni, sitten sanasta sanaan -käännös ja lopuksi koko kohtaus putkeen ilman apuja.

## Alkusoitto: varkaus

Kuningas Viserysin neuvostossa lohikäärmeenvartijoiden vanhin kertoo, mitä yöllä tapahtui:

<Line id="hotd-s1e02-001" />

<Interlinear hv="Laodikio īlot upȳdas." gloss="varas-NOM me-DAT pakoon.päästä-PRF.3SG" fi="Varas pääsi meiltä karkuun." />

<Hv glossed>Laodikio</Hv> ’varas’ on rakennettu verbistä <Hv glossed>laodigon</Hv> ’varastaa’ tekijäjohtimella (<LessonLink n={48}>oppitunti 48</LessonLink>). <Hv glossed>Upyghagon</Hv> ’päästä pakoon jostakin’ -verbin kanssa se, jolta päästään pakoon, on datiivissa (<Hv>īlot</Hv>). Ajankohta on <Hv glossed>Masso Jēdunna</Hv> ’Lepakon hetkenä’. Sanaan <Hv l="jēdun">jēdun</Hv> tutustuit <LessonLink n={46}>oppitunnilla 46</LessonLink>, ja <Hv glossed>masso</Hv> ’lepakon’ tulee vastaan uudelleen Petersonin päiväyksessä (<LessonLink n={41}>oppitunti 41</LessonLink>). Alun <Hv glossed>sȳndrorro</Hv> on ’pimeydessä’, kollektiivin lokatiivi sanasta <Hv l="sȳndror">sȳndror</Hv>.

<Line id="hotd-s1e02-002" />

<Interlinear hv="Skorkydoso zaldrīzo drōmon tōmēpsa urneroti rȳ laodikson kōttas?" gloss="miten lohikäärme-GEN muna-NOM viisikymmentä vartija-GEN.PL keskeltä varastaa-PASS.INF voida-PRF.3SG" fi="Miten lohikäärmeen muna on voitu varastaa viidenkymmenen vartijan keskeltä?" />

Kuninkaan kysymyksessä on passiivin infinitiivi: muna on subjekti, ja se *voi tulla varastetuksi* (<Hv glossed>laodikson kōttas</Hv>). <Hv>Kōttas</Hv> on verbin <Hv>kostagon</Hv> epäsäännöllinen perfekti, joka on tuttu Duolingon infinitiiviosiosta (<LessonLink n={27}>oppitunti 27</LessonLink>). Postposition <Hv>rȳ</Hv> edellä on genetiivi (<LessonLink n={49}>oppitunti 49</LessonLink>).

## Hovissa: mikä muna?

Ennen matkaa Rhaenyra kuulustelee lohikäärmeenvartijoiden vanhinta.

<Line id="hotd-s1e02-006" />

<Line id="hotd-s1e02-007" />

<Interlinear hv="Skorior drōmon Daemon lāettas?" gloss="mikä-TERR.ACC muna-ACC Daemon-NOM varastaa-PRF.3SG" fi="Minkä munan Daemon vei?" />

Kysyvä adjektiivi tuo substantiivin mukanaan lauseen alkuun (<LessonLink n={47}>oppitunti 47</LessonLink>). <Hv>Drōmon</Hv> on maasukuinen, joten muoto on <Hv l="skore">skorior</Hv>. <Hv glossed>Uēpys</Hv> ’vanhin’ on adjektiivin <Hv>uēpa</Hv> vokatiivi, ja sitä käytetään puhuttelusanana.

<Line id="hotd-s1e02-008" />

<Interlinear hv="Dārilaro Baelo mazumbille iderēptā hēnkon luon." gloss="prinssi-GEN Baelon-GEN kehto-ACC valita-PRF.2SG sama-TERR.NOM joka-TERR.NOM" fi="Sama, jonka valitsit prinssi Baelonin kehtoon." />

Sanakirjan mukaan <Hv glossed>iderēbagon</Hv> on ’valita jotakin (datiivi) jollekulle (akkusatiivi)’, joten kehto, jolle muna valittiin, on akkusatiivissa. Lopussa on harvinainen sanajärjestys: <Hv glossed>hēnkon luon</Hv>, *sama, joka*. Relatiiviadjektiivi tulee viimeiseksi, eikä sen jäljessä ole substantiivia. Wiki ei selitä tätä rakennetta erikseen (<LessonLink n={46}>oppitunti 46</LessonLink>). Ensimmäisen lauseen <Hv l="sagon" glossed>īles</Hv> on <Hv glossed>sagon</Hv>-verbin ’olla’ imperfekti: *muna oli Dreamfyren*.

## Lohikäärmekivellä

<Line id="hotd-s1e02-009" />

<Interlinear hv="Kepa ñuha yne Zaldrīzdōro Dārilaroso brōstas." gloss="isä-NOM minun-LUN.NOM minä-ACC Lohikäärmekivi-GEN prinsessa-INS nimetä-PRF.3SG" fi="Isäni nimitti minut Lohikäärmekiven prinsessaksi." />

<Hv glossed>Brōzagon</Hv> ’nimetä’ -verbin kanssa nimetty on akkusatiivissa ja nimi **instrumentaalissa**. Suomessa tässä käytetään translatiivia (*prinsessaksi*), valyriassa instrumentaalia. Toisen lauseen relatiivirakenteen kävit läpi <LessonLink n={46}>oppitunnilla 46</LessonLink>: <Hv glossed>Kesy uglaesā luon sombāzmion ñuhon issa</Hv>, *tämä on minun linnani, jossa asut*. Lisäksi repliikissä on <Hv>kepus</Hv>, sanan <Hv>kepa</Hv> vokatiivi. Sana tarkoittaa sekä isää että isän veljeä (Duolingon perheosio, <LessonLink n={11}>oppitunti 11</LessonLink>), joten Rhaenyra puhuttelee setäänsä Daemonia samalla sanalla, jolla hän puhuu isästään.

<Line id="hotd-s1e02-010" />

Tämän kävit läpi <LessonLink n={46}>oppitunnilla 46</LessonLink>: <Hv fi="ennen kuin">iā</Hv> + subjunktiivi, *ei ennen kuin olet täysi-ikäinen*. <Hv glossed>Sīlāvose</Hv> on ’varma’ mutta myös ’täysi-ikäinen’.

<Line id="hotd-s1e02-011" />

<Interlinear hv="Vēdroso dāri aōhi iqīdā." gloss="viha-INS kuningas-ACC sinun-SOL.ACC kuormittaa-PRF.2SG" fi="Olet suututtanut kuninkaasi." />

Sanatarkasti: *olet kuormittanut kuninkaasi vihalla*. Sanakirjan mukaan <Hv glossed>iqighagon</Hv> on ’kuormata jotakin (akkusatiivi) jollakin (instrumentaali)’ ja kuvaannollisesti ’aiheuttaa jollekulle jotakin’. Tämä on hyvä esimerkki siitä, ettei valyriaa voi kääntää sana sanalta. Kannattaa opetella koko ilmaus.

<Line id="hotd-s1e02-012" />

<Interlinear hv="Indirarion urnion daor. Tubī manaeri. Nyke dīnilaks." gloss="syy-ACC nähdä-PRS.SBJV.1SG ei tänään juhlia-PRS.1PL minä-NOM vihkiä-FUT.PASS.1SG" fi="En näe syytä. Tänään juhlitaan. Minut vihitään." />

Kolme lyhyttä lausetta, joissa on kolme eri kielioppiasiaa: kielto ja subjunktiivi (<Hv fi="en näe">urnion daor</Hv>), monikon 1. persoona (<Hv glossed>manaeri</Hv>, *me juhlimme*) ja passiivin futuuri (<Hv glossed>dīnilaks</Hv>, *minut vihitään*). Sanakirjan mukaan <Hv glossed>dīnagon</Hv> on perusmerkitykseltään ’panna, asettaa’, mutta vihkijästä puhuttaessa ’vihkiä joku jonkun kanssa’. Siksi passiivissa se tarkoittaa *tulla vihityksi*. Daemon käyttää lisäksi painottavaa pronominia <Hv>nyke</Hv>.

<Line id="hotd-s1e02-013" />

<Line id="hotd-s1e02-014" />

Tämän sananvaihdon kävit läpi <LessonLink n={46}>oppitunnilla 46</LessonLink>. Rhaenyran <Hv glossed>ābrazȳri</Hv> ’vaimo’ on akkusatiivissa, ja Daemonin <Hv l="lȳ">lī</Hv> jatkaa samaa akkusatiivia: *(en) sellaista, jonka minä olisin valinnut*. <Hv>Ābrazȳrys</Hv> on yhdyssana (<Hv>ābra</Hv> + <Hv>zȳrys</Hv>, <LessonLink n={48}>oppitunti 48</LessonLink>).

<Line id="hotd-s1e02-015" />

<Interlinear hv="Se kejo syt ñuho valonqro drōmon laodigon aō bēviltas?" gloss="ja tämä-GEN varten minun-GEN pikkuveli-GEN muna-ACC varastaa-INF sinä-GEN täytyä-PRF.3SG" fi="Ja tämänkö vuoksi sinun piti varastaa veljeni muna?" />

<Note>
Repliikissä on *ñuho valonqro*, mutta sanakirjassa <Hv>valonqar</Hv> on vesisukuinen, joten odotettu muoto olisi <Hv fi="veljeni">ñurho valonqro</Hv>, kuten Rhaenyran toisessa repliikissä veljestään (<LessonLink n={11}>oppitunti 11</LessonLink>). Peterson ei ole kommentoinut tätä.
</Note>

Duolingon infinitiiviosiossa (<LessonLink n={27}>oppitunti 27</LessonLink>) opit, että <Hv glossed>bēvilagon</Hv> ’täytyä’ -verbin kanssa tekijä on genetiivissä juuri verbin edellä: <Hv glossed>aō bēviltas</Hv>, *sinun täytyi*. Infinitiivi tulee sen edellä. <Hv glossed>Kejo syt</Hv> on sanakirjassa sama ilmaus kuin <Hv glossed>kesrio syt</Hv> (<LessonLink n={46}>oppitunti 46</LessonLink>). Tässä se tarkoittaa sanatarkasti *tämän vuoksi*.

<Line id="hotd-s1e02-016" />

<Interlinear hv="Bībot zaldrīzome mazumbillo vulē." gloss="vauvaikä-LOC lohikäärme-COM kehto-GEN maata-IPFV.2SG" fi="Vauvana makasit kehdossa lohikäärmeen kanssa." />

<Hv glossed>Vilagon</Hv> ’olla, maata jossakin’ -verbin kanssa paikka on datiivissa. Sanakirjan mukaan yksikössä voi datiivin sijaan käyttää genetiiviä, kun paikka on suoraan verbin edellä, ja niin tässä tehdään (<Hv fi="makasit kehdossa">mazumbillo vulē</Hv>). Verbi alkaa myös konsonantilla, kuten <LessonLink n={33}>oppitunnin 33</LessonLink> sääntö edellyttää. Komitatiivi <Hv glossed>zaldrīzome</Hv> ’lohikäärmeen kanssa’ on suomen komitatiivin serkku (*lohikäärmeineen*), mutta valyriassa sitä käytetään paljon vapaammin.

<Interlinear hv="Konir ñuho rūho syt jaelan." gloss="se-ACC minun-GEN vauva-GEN varten haluta-PRS.1SG" fi="Haluan samaa lapselleni." />

<Line id="hotd-s1e02-017" />

<Hv glossed>Sikilāt</Hv> on verbin <Hv glossed>sikagon</Hv> ’synnyttää, saada lapsi’ futuurin monikon 2. persoona. Muodosta päätellen Rhaenyra kysyy Daemonilta ja tämän morsiamelta yhdessä: *saatteko te lapsen?*

<Line id="hotd-s1e02-018" />

<Interlinear hv="Māzīlarē tubī." gloss="tuleva-LOC päivä-LOC" fi="Jonakin päivänä." />

<Hv glossed>Māzīlare</Hv> on verbin <Hv glossed>māzigon</Hv> ’tulla’ futuurin partisiippi, *tuleva*. *Tulevana päivänä* tarkoittaa siis *jonakin päivänä*. Daemon antaa Rhaenyralle munan takaisin, ja kohtaus päättyy.

## Lue kohtaus uudelleen

Lukekaa nyt Lohikäärmekiven repliikit (009–018) putkeen, mieluiten ääneen kahdestaan: toinen on Rhaenyra, toinen Daemon. Kiinnittäkää huomiota siihen, miten lyhyitä repliikit ovat. Keskustelussa ei tarvita pitkiä relatiivilauseketjuja. Riittää yksi verbi, kielto ja joskus vokatiivi.

## Hahmossa: kaksi Targaryenia

Teitä on kaksi, ja niin on Rhaenyraa ja Daemoniakin. Seitsemännessä jaksossa Rhaenyra kosii Daemonia. Repliikit sopivat sellaisinaan pariskunnalle:

<Line id="hotd-s1e07-012" />

<Line id="hotd-s1e07-013" />

Ensimmäisessä on kehotus *tehdään*: <Hv fi="sitokaamme">īlot letagon</Hv> on <Hv>īlot</Hv> ja infinitiivi (<LessonLink n={42}>oppitunti 42</LessonLink>). Toisessa <Hv fi="sinä ja minä">ao nykē</Hv> on tuttu ja-pidennys (<LessonLink n={2}>oppitunti 2</LessonLink>): sanan <Hv>nyke</Hv> pitkä loppuvokaali tarkoittaa ’ja’.

Daemonin arvonimet kuullaan toisella kaudella nuoren Rhaenyran suusta:

<Example id="ex-27cf31bdb15b" />

Ja näin Daemonia puhutellaan, kun hänet halutaan nolata:

<Example id="ex-f1f08370505a" />

Lyhyin esittely on nimi ja <Hv glossed>iksan</Hv> ’olen’, kuten Daenerysilla (<LessonLink n={49}>oppitunti 49</LessonLink>):

<Practice hv="Daemon Targārien iksan." fi="Olen Daemon Targaryen." />

<Practice hv="Rhaenyra Targārien iksan." fi="Olen Rhaenyra Targaryen." />

## Ääntämisharjoitus ilman äänitettä

House of the Dragonin repliikeistä ei ole Petersonin äänitteitä, eivätkä sarjan näyttelijöiden äänitteet ole ääntämisen malli. Harjoittele siksi painosäännön avulla (<LessonLink n={1}>oppitunti 1</LessonLink>): katso jokaisen vaikean sanan paino sanakirjasta tai alta, sano repliikki hitaasti ja nauhoita se. Vertaa sitten sanoihin, joista Petersonin äänite on olemassa.

<Pronounce l="drakarys" word="drakarys" />

<Pronounce word="Targārien" />

## Käsikirjoitus ääneen

Jatkakaa käsikirjoituksen harjoittelua yhdessä. Toisen repliikki on sinulle merkki siitä, että on sinun vuorosi. Jos jokin repliikki ei enää tunnu omalta, hylätkää se. Hylätyn repliikin voi palauttaa myöhemmin Hylätyt-listasta.

<HalloweenScript />

## Kokoa itse

<Practice hv="Valzȳri emā?" fi="Onko sinulla aviomies?" />

<Practice hv="Ābrazȳri sīr emā?" fi="Onko sinulla jo vaimo?" />

<Practice hv="Māzīlarē tubī Zaldrīzdōrot jagon jaelan." fi="Jonakin päivänä haluan mennä Lohikäärmekivelle." />

<Vocab />

<Check />
